import { useCallback, useEffect, useState } from "react";

import type {
  InitiatePaymentDto,
  PaymentPlan,
  PaymentStatusResponse,
} from "@/client/services/payment";
import { paymentService } from "@/client/services/payment";

export type PaymentStep = "idle" | "selecting" | "processing" | "waiting" | "success" | "failed";

export type PaymentState = {
  step: PaymentStep;
  selectedPlan: "ONE_TIME_DOWNLOAD" | "MONTHLY_SUBSCRIPTION" | null;
  email: string;
  paymentId: string | null;
  paymentStatus: PaymentStatusResponse | null;
  error: string | null;
  availablePlans: PaymentPlan[];
  plansLoading: boolean;
};

const defaultState: PaymentState = {
  step: "idle",
  selectedPlan: null,
  email: "",
  paymentId: null,
  paymentStatus: null,
  error: null,
  availablePlans: [],
  plansLoading: true,
};

export const usePayment = () => {
  const [state, setState] = useState<PaymentState>(defaultState);

  // Fetch available payment plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plans = await paymentService.getPaymentPlans();
        setState((prev) => ({
          ...prev,
          availablePlans: plans,
          plansLoading: false,
        }));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch payment plans:", error);
        setState((prev) => ({
          ...prev,
          plansLoading: false,
          error: "Failed to load payment plans. Please refresh the page.",
        }));
      }
    };
    void fetchPlans();
  }, []);

  const initiatePayment = useCallback(
    async (planType: "ONE_TIME_DOWNLOAD" | "MONTHLY_SUBSCRIPTION", email: string) => {
      setState((prev) => ({
        ...prev,
        step: "processing",
        selectedPlan: planType,
        email,
        error: null,
      }));

      try {
        // Map plan type to database plan name
        const planNameMap: Record<
          "ONE_TIME_DOWNLOAD" | "MONTHLY_SUBSCRIPTION",
          "one_time" | "subscription"
        > = {
          ONE_TIME_DOWNLOAD: "one_time",
          MONTHLY_SUBSCRIPTION: "subscription",
        };

        const planName = planNameMap[planType];
        const plan = state.availablePlans.find((p) => p.name === planName);

        if (!plan) {
          throw new Error("Selected plan not found. Please refresh and try again.");
        }

        // Prepare payment data with plan UUID
        const paymentData: InitiatePaymentDto = {
          planId: plan.id,
          email,
        };

        const response = await paymentService.initiatePayment(paymentData);

        setState((prev) => ({
          ...prev,
          step: "waiting",
          paymentId: response.paymentId,
        }));

        // Redirect to Pesapal checkout
        window.location.href = response.redirectUrl;
      } catch (error: unknown) {
        const errorMessage =
          (error as { response?: { data?: { message?: string } } }).response?.data?.message ??
          (error as Error).message;
        setState((prev) => ({
          ...prev,
          step: "failed",
          error: errorMessage,
        }));
      }
    },
    [state.availablePlans],
  );

  const pollPaymentStatus = useCallback((paymentId: string) => {
    const maxAttempts = 30; // 60 seconds total (30 * 2 seconds)
    let attempts = 0;

    const interval = setInterval(async () => {
      attempts++;

      if (attempts > maxAttempts) {
        clearInterval(interval);
        setState((prev) => ({
          ...prev,
          step: "failed",
          error: "Payment timeout. Please check your phone and try again.",
        }));
        return;
      }

      try {
        const status = await paymentService.checkStatus(paymentId);

        if (status.status === "SUCCESSFUL") {
          clearInterval(interval);
          setState((prev) => ({
            ...prev,
            step: "success",
            paymentStatus: status,
          }));
        } else if (status.status === "FAILED") {
          clearInterval(interval);
          setState((prev) => ({
            ...prev,
            step: "failed",
            error: "Payment failed. Please try again.",
            paymentStatus: status,
          }));
        }
        // If still PENDING, continue polling
      } catch {
        // Don't fail on network errors during polling, just continue
        if (attempts === maxAttempts) {
          clearInterval(interval);
          setState((prev) => ({
            ...prev,
            step: "failed",
            error: "Unable to verify payment status. Please check your account.",
          }));
        }
      }
    }, 2000); // Poll every 2 seconds
  }, []);

  const reset = useCallback(() => {
    setState((prev) => ({
      ...defaultState,
      availablePlans: prev.availablePlans, // Keep loaded plans
      plansLoading: false,
    }));
  }, []);

  // Helper to get plan details
  const getPlanDetails = useCallback(
    (planType: "ONE_TIME_DOWNLOAD" | "MONTHLY_SUBSCRIPTION") => {
      const planNameMap: Record<
        "ONE_TIME_DOWNLOAD" | "MONTHLY_SUBSCRIPTION",
        "one_time" | "subscription"
      > = {
        ONE_TIME_DOWNLOAD: "one_time",
        MONTHLY_SUBSCRIPTION: "subscription",
      };
      const planName = planNameMap[planType];
      return state.availablePlans.find((p) => p.name === planName);
    },
    [state.availablePlans],
  );

  return {
    state,
    initiatePayment,
    pollPaymentStatus,
    reset,
    getPlanDetails,
  };
};
